import { forwardRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { AlertCircle } from "lucide-react";
import { submitForm } from "../../api/public.api.js";

const FORM_TYPE_OPTIONS = [
  { value: "design_inquiry", label: "Design enquiry" },
  { value: "new_build", label: "New build enquiry" },
  { value: "preconstruction", label: "Pre-construction" },
  { value: "maintenance", label: "Maintenance" },
  { value: "appointment", label: "Book a visit" },
  { value: "career", label: "Career application" },
  { value: "contact_general", label: "General enquiry" },
];

const schema = z.object({
  formType: z.string().min(1),
  name: z.string().trim().min(1, "Please enter your name"),
  email: z.string().trim().email("Enter a valid email"),
  phone: z.string().trim().min(6, "Enter a phone number").max(40),
  message: z.string().max(2000).optional().or(z.literal("")),
});

/**
 * Inquiry form embedded on design / display home / service / contact pages.
 *
 * The parent passes the *initial* `formType` (e.g. "new_build") and any
 * prefilled data — visitors then see a "Type of enquiry" select pre-set to
 * that value, but they can change it before submitting if their enquiry
 * doesn't match the page they landed on.
 */
export function InquiryForm({
  formType: initialFormType = "design_inquiry",
  prefill = {},
  sourcePage,
  className,
  successMessage = "Thanks — we'll be in touch within one business day.",
}) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isSubmitSuccessful },
    reset,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      formType: initialFormType,
      name: "",
      email: "",
      phone: "",
      message: "",
    },
  });

  const onSubmit = async (values) => {
    const { formType, ...rest } = values;
    // Trim every string field so trailing whitespace can't trip the
    // backend's min(1) checks — the user-side schema already trims, but
    // belt-and-braces here makes the payload self-consistent.
    const data = Object.fromEntries(
      Object.entries(rest).map(([k, v]) => [k, typeof v === "string" ? v.trim() : v])
    );
    try {
      await submitForm({
        formType,
        // Spread prefill first so page-supplied context (designName, etc.)
        // doesn't overwrite name/email/phone the user actually filled.
        data: { ...prefill, ...data },
        sourcePage:
          sourcePage ?? (typeof window !== "undefined" ? window.location.pathname : ""),
      });
      toast.success(successMessage);
      reset({
        formType,
        name: "",
        email: "",
        phone: "",
        message: "",
      });
    } catch (err) {
      const details = err?.response?.data?.details;
      if (Array.isArray(details) && details.length > 0) {
        toast.error(details.map((d) => `${d.path}: ${d.message}`).join("\n"));
      } else {
        toast.error(
          err?.response?.data?.message ?? "Submission failed — please try again."
        );
      }
      console.error("[InquiryForm] submit failed", {
        formType,
        dataKeys: Object.keys(data),
        status: err?.response?.status,
        response: err?.response?.data,
      });
    }
  };

  if (isSubmitSuccessful && !isSubmitting) {
    return (
      <div className={className}>
        <div className="rounded-2xl border border-success/30 bg-success/10 p-6 text-center">
          <p className="font-display text-lg text-text-primary">Thanks!</p>
          <p className="mt-1 text-sm text-text-secondary">{successMessage}</p>
        </div>
      </div>
    );
  }

  // Surface every validation error as a summary up top so visitors don't
  // miss a missing field hidden below the fold.
  const errorList = Object.entries(errors)
    .map(([k, v]) => v?.message && { key: k, message: v.message })
    .filter(Boolean);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={className} noValidate>
      <div className="space-y-3">
        {errorList.length > 0 && (
          <div className="flex items-start gap-2 rounded-md border border-danger/30 bg-danger/10 p-3 text-xs text-danger">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <p className="font-medium">Please fix the following:</p>
              <ul className="mt-1 list-disc pl-4">
                {errorList.map((e) => (
                  <li key={e.key}>{e.message}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        <Select
          label="Type of enquiry"
          required
          error={errors.formType?.message}
          {...register("formType")}
        >
          {FORM_TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>

        <Field label="Name" required error={errors.name?.message} {...register("name")} />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field
            label="Email"
            type="email"
            required
            error={errors.email?.message}
            {...register("email")}
          />
          <Field
            label="Phone"
            type="tel"
            required
            error={errors.phone?.message}
            {...register("phone")}
          />
        </div>
        <Textarea label="Message (optional)" rows={3} {...register("message")} />
        <button type="submit" disabled={isSubmitting} className="btn-primary w-full">
          {isSubmitting ? "Sending…" : "Send enquiry"}
        </button>
        <p className="text-center text-xs text-text-muted">
          By submitting, you agree to be contacted about your enquiry.
        </p>
      </div>
    </form>
  );
}

// forwardRef is *required* — without it, react-hook-form's `register()` ref
// doesn't reach the underlying input/select/textarea, and the form thinks
// every field is empty (showing "Required" for every field, even after the
// user fills them in).
const Field = forwardRef(function Field({ label, error, required, ...props }, ref) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium uppercase tracking-wider text-text-secondary">
        {label}
        {required && <span className="ml-1 text-danger">*</span>}
      </span>
      <input
        ref={ref}
        {...props}
        className={`w-full rounded-md border bg-bg-elevated-2 px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent ${
          error ? "border-danger" : "border-border"
        }`}
      />
      {error && <span className="mt-1 block text-xs text-danger">{error}</span>}
    </label>
  );
});

const Select = forwardRef(function Select(
  { label, error, required, children, ...props },
  ref
) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium uppercase tracking-wider text-text-secondary">
        {label}
        {required && <span className="ml-1 text-danger">*</span>}
      </span>
      <select
        ref={ref}
        {...props}
        className={`w-full rounded-md border bg-bg-elevated-2 px-3 py-2 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent ${
          error ? "border-danger" : "border-border"
        }`}
      >
        {children}
      </select>
      {error && <span className="mt-1 block text-xs text-danger">{error}</span>}
    </label>
  );
});

const Textarea = forwardRef(function Textarea({ label, ...props }, ref) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium uppercase tracking-wider text-text-secondary">
        {label}
      </span>
      <textarea
        ref={ref}
        {...props}
        className="w-full rounded-md border border-border bg-bg-elevated-2 px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
      />
    </label>
  );
});
